import { useMemo, useState } from "react";
import { motion, Reorder } from "framer-motion";
import { CheckCircle2, RotateCcw, GripVertical } from "lucide-react";
import { STORY_STEPS } from "../../lib/constants";
import { submitScore } from "../../lib/api";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function StoryBuilder() {
  const [items, setItems] = useState(() => shuffle(STORY_STEPS));
  const [checked, setChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const correctOrder = useMemo(() => items.every((it, idx) => it.id === STORY_STEPS[idx].id), [items]);

  const verify = () => {
    setChecked(true);
    if (correctOrder && !submitted) {
      submitScore("story-builder", "guest", 120).catch(() => {});
      setSubmitted(true);
    }
  };

  const reset = () => {
    setItems(shuffle(STORY_STEPS));
    setChecked(false);
    setSubmitted(false);
  };

  return (
    <div data-testid="story-builder" className="w-full">
      <p className="text-sm text-[#1A1414]/70 mb-4">
        Drag the steps into the right order to defuse an online threat. The clock starts now.
      </p>

      <Reorder.Group
        axis="y"
        values={items}
        onReorder={(arr) => {
          setItems(arr);
          setChecked(false);
        }}
        className="space-y-2"
      >
        {items.map((item, idx) => {
          const isInCorrectSlot = checked && item.id === STORY_STEPS[idx].id;
          const isWrongSlot = checked && !isInCorrectSlot;
          return (
            <Reorder.Item
              key={item.id}
              value={item}
              data-testid={`story-step-${item.id}`}
              className={`flex items-center gap-3 p-4 rounded-2xl border-2 bg-white cursor-grab active:cursor-grabbing shadow-arc ${
                isInCorrectSlot
                  ? "border-emerald-400"
                  : isWrongSlot
                  ? "border-[#E60000]"
                  : "border-[#FF8080]/30 hover:border-[#FF8080]"
              }`}
              whileDrag={{ scale: 1.03, boxShadow: "0 18px 40px rgba(139,0,0,0.25)" }}
            >
              <GripVertical size={18} className="text-[#1A1414]/40 shrink-0" />
              <span className="font-display font-black text-2xl text-[#FF8080] w-8 shrink-0">
                {idx + 1}
              </span>
              <span className="text-sm sm:text-base text-[#1A1414] font-medium leading-snug">
                {item.text}
              </span>
              {isInCorrectSlot && (
                <CheckCircle2 size={20} className="text-emerald-500 ml-auto shrink-0" />
              )}
            </Reorder.Item>
          );
        })}
      </Reorder.Group>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={verify}
          data-testid="story-verify-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E60000] text-white font-display font-bold shadow-arc hover:bg-[#8B0000] hover:-translate-y-0.5 transition-all"
        >
          Verify order
        </button>
        <button
          onClick={reset}
          data-testid="story-reset-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#E60000] border-2 border-[#E60000] font-display font-bold hover:bg-[#FF8080]/15 transition-all"
        >
          <RotateCcw size={16} /> Reshuffle
        </button>
      </div>

      {checked && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-5 p-4 rounded-2xl text-sm ${
            correctOrder
              ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
              : "bg-[#FF8080]/15 text-[#8B0000] border border-[#E60000]/30"
          }`}
          data-testid="story-feedback"
        >
          {correctOrder
            ? "Perfect sequence. You just walked through the exact protocol used by trusted cyber‑safety counselors. +120 Hero Stones."
            : "Not quite. The bigger move is always to STOP first, then capture evidence, then block, then tell a trusted adult — never the other way around."}
        </motion.div>
      )}
    </div>
  );
}
